import React, { useState, useEffect, useCallback } from 'react';
import { Search, Building2, MapPin, Check, AlertCircle, Globe, Info } from 'lucide-react';
import { Modal } from '@/components/ui/Modal/Modal';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button/Button';
import { Badge } from '@/components/ui/Badge/Badge';
import { LoadingState } from '@/components/ui/LoadingState';
import { VStack, HStack } from '@/components/ui/Stack';
import { VenuesService } from '@/services/VenuesService';
import { GooglePlacesSearchService, type HotelSearchResult } from '@/services/GooglePlacesSearchService';
import { useNotificationStore } from '@/stores/notificationStore';

interface AddHotelModalProps {
  isOpen: boolean;
  onClose: () => void;
  onHotelAdded: (venue: { id: string; property_name: string }) => void;
  shortlistedVenueIds: Set<string>;
  isAtMaxShortlist: boolean;
}

interface SearchResultWithMatch extends HotelSearchResult {
  matchedVenue?: {
    id: string;
    property_name: string;
  };
}

export function AddHotelModal({
  isOpen,
  onClose,
  onHotelAdded,
  shortlistedVenueIds,
  isAtMaxShortlist,
}: AddHotelModalProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<SearchResultWithMatch[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importingIds, setImportingIds] = useState<Set<string>>(new Set());

  const venuesService = new VenuesService();
  const placesService = new GooglePlacesSearchService();
  const notificationStore = useNotificationStore();

  const handleSearch = useCallback(async () => {
    const query = searchTerm.trim();
    if (!query) return;

    setLoading(true);
    setHasSearched(true);
    setError(null);

    try {
      const searchResponse = await placesService.searchHotels({
        query,
        pageSize: 20,
      });

      // Check which place_ids already exist in our database
      const placeIds = searchResponse.hotels.map(h => h.placeId).filter(Boolean);
      let existingVenues: Record<string, { id: string; property_name: string }> = {};

      if (placeIds.length > 0) {
        try {
          const checkResult = await placesService.checkExistingVenues(placeIds);
          existingVenues = checkResult.existing || {};
        } catch (err) {
          console.error('Failed to check existing venues:', err);
        }
      }

      const combinedResults: SearchResultWithMatch[] = searchResponse.hotels.map(hotel => ({
        ...hotel,
        matchedVenue: existingVenues[hotel.placeId] ? {
          id: existingVenues[hotel.placeId].id,
          property_name: existingVenues[hotel.placeId].property_name,
        } : undefined,
      }));

      setResults(combinedResults);
    } catch (err) {
      console.error('Failed to search hotels:', err);
      setError(err instanceof Error ? err.message : 'Failed to search hotels');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [searchTerm]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !loading) {
      e.preventDefault();
      handleSearch();
    }
  };

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setResults([]);
      setHasSearched(false);
      setLoading(false);
      setError(null);
      setImportingIds(new Set());
    }
  }, [isOpen]);

  async function handleSelectResult(result: SearchResultWithMatch) {
    const isAlreadyShortlisted = result.matchedVenue && shortlistedVenueIds.has(result.matchedVenue.id);
    if (isAlreadyShortlisted || isAtMaxShortlist) return;

    if (result.matchedVenue) {
      // Venue exists in database — just add to shortlist
      onHotelAdded({ id: result.matchedVenue.id, property_name: result.matchedVenue.property_name });
      onClose();
    } else {
      // Venue doesn't exist — import from Google Places, then add
      setImportingIds(prev => new Set(prev).add(result.placeId));
      setError(null);

      try {
        const importResult = await placesService.importHotels([{
          placeId: result.placeId,
          name: result.name,
          address: result.address,
          lat: result.lat,
          lon: result.lon,
          rating: result.rating,
          userRatingCount: result.userRatingCount,
          phone: result.phone,
          website: result.website,
        }]);

        let venueId: string | undefined;

        if (importResult.imported === 0) {
          // Race condition — already exists. Look it up.
          const checkResult = await placesService.checkExistingVenues([result.placeId]);
          const existing = checkResult.existing[result.placeId];
          if (existing) {
            venueId = existing.id;
          } else {
            setError('Failed to import hotel. It may already exist.');
            return;
          }
        } else {
          venueId = importResult.importedIds[0];
        }

        if (!venueId) {
          setError('Failed to import hotel: no venue ID returned');
          return;
        }

        // Trigger AI enrichment (fire-and-forget)
        try {
          await venuesService.triggerEnrichment(venueId);
        } catch (enrichError) {
          console.warn('Failed to trigger enrichment:', enrichError);
        }

        notificationStore.success(`Imported ${result.name} and triggered AI research`, {
          title: 'Hotel Imported',
        });

        onHotelAdded({ id: venueId, property_name: result.name });
        onClose();
      } catch (err) {
        console.error('Failed to import hotel:', err);
        setError(err instanceof Error ? err.message : 'Failed to import hotel. Please try again.');
      } finally {
        setImportingIds(prev => {
          const next = new Set(prev);
          next.delete(result.placeId);
          return next;
        });
      }
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Hotel to Shortlist"
      size="lg"
    >
      <VStack gap={4} className="min-h-0 sm:min-h-[400px]">
        {isAtMaxShortlist && (
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start gap-2">
            <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-700">Shortlist is full (20/20). Remove a venue to add more.</p>
          </div>
        )}

        <HStack gap={2}>
          <Input
            placeholder="Search hotel name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
            leftIcon={<Search className="w-4 h-4" />}
            disabled={loading}
          />
          <Button
            onClick={handleSearch}
            isLoading={loading}
            disabled={!searchTerm.trim() || loading}
          >
            Search
          </Button>
        </HStack>

        {error && (
          <div className="bg-error-50 border border-error-200 rounded-md p-3 flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-error-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-error-700">{error}</p>
          </div>
        )}

        <div className="flex-1 overflow-y-auto border rounded-md min-h-[200px] sm:min-h-[300px] max-h-[60vh] sm:max-h-[400px]">
          {loading ? (
            <div className="h-full flex items-center justify-center py-12">
              <LoadingState text="Searching Google Places..." />
            </div>
          ) : results.length > 0 ? (
            <div className="divide-y">
              {results.map((result) => {
                const isImporting = importingIds.has(result.placeId);
                const isInDatabase = !!result.matchedVenue;
                const isAlreadyShortlisted = isInDatabase && shortlistedVenueIds.has(result.matchedVenue!.id);
                const isDisabled = isImporting || isAlreadyShortlisted || isAtMaxShortlist;

                return (
                  <button
                    key={result.placeId}
                    className={`w-full text-left p-3 sm:p-4 transition-colors group ${
                      isDisabled ? 'opacity-60 cursor-not-allowed' : 'hover:bg-gray-50'
                    }`}
                    onClick={() => handleSelectResult(result)}
                    disabled={isDisabled}
                  >
                    <div className="flex items-start gap-2 sm:gap-3">
                      {isInDatabase ? (
                        <Building2 className="w-5 h-5 mt-0.5 flex-shrink-0 text-primary-600" />
                      ) : (
                        <Globe className="w-5 h-5 mt-0.5 flex-shrink-0 text-gray-400" />
                      )}
                      <div className="flex-1 min-w-0">
                        {/* Name + badge */}
                        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mb-1">
                          <span className="font-medium text-gray-900 break-words">{result.name}</span>
                          {isAlreadyShortlisted && (
                            <Badge variant="secondary" className="text-[10px] sm:text-xs flex-shrink-0">
                              Already Added
                            </Badge>
                          )}
                          {isInDatabase && !isAlreadyShortlisted && (
                            <Badge variant="success" className="text-[10px] sm:text-xs flex-shrink-0">
                              In Database
                            </Badge>
                          )}
                        </div>
                        {/* Address */}
                        <div className="text-xs sm:text-sm text-gray-500 flex items-start gap-1 mb-1">
                          <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
                          <span className="break-words">{result.address}</span>
                        </div>
                        {/* Rating */}
                        {result.rating && (
                          <div className="text-xs text-gray-400">
                            ⭐ {result.rating.toFixed(1)} {result.userRatingCount ? `(${result.userRatingCount.toLocaleString()} reviews)` : ''}
                          </div>
                        )}
                        {/* Mobile action button — always visible */}
                        <div className="mt-2 sm:hidden">
                          <Button
                            size="sm"
                            variant="primary"
                            rightIcon={<Check className="w-3.5 h-3.5" />}
                            isLoading={isImporting}
                            disabled={isDisabled}
                            className="text-xs h-7"
                          >
                            {isImporting ? 'Importing...' : isAlreadyShortlisted ? 'Added' : isInDatabase ? 'Add to Shortlist' : 'Import & Add'}
                          </Button>
                        </div>
                      </div>
                      {/* Desktop action button — hover reveal */}
                      <div className={`hidden sm:block flex-shrink-0 ml-2 transition-opacity ${
                        isDisabled ? 'opacity-50' : 'opacity-0 group-hover:opacity-100'
                      }`}>
                        <Button
                          size="sm"
                          variant="primary"
                          rightIcon={<Check className="w-4 h-4" />}
                          isLoading={isImporting}
                          disabled={isDisabled}
                        >
                          {isImporting ? 'Importing...' : isAlreadyShortlisted ? 'Added' : isInDatabase ? 'Add to Shortlist' : 'Import & Add'}
                        </Button>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : hasSearched ? (
            <div className="h-full flex flex-col items-center justify-center text-gray-500 gap-2 py-12">
              <Search className="w-8 h-8 opacity-20" />
              <p>No hotels found matching "{searchTerm}"</p>
              <p className="text-xs text-gray-400">Try a different search term</p>
            </div>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-gray-500 gap-2 py-12">
              <Search className="w-8 h-8 opacity-20" />
              <p className="text-sm">Search for a hotel by name to add it to your shortlist</p>
              <p className="text-xs text-gray-400">Hotels not yet in your database will be imported automatically</p>
            </div>
          )}
        </div>
      </VStack>
    </Modal>
  );
}
